import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { authManager } from '../utils/auth';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Base_Url = process.env.REACT_APP_API_BASE_URL;

const Category_Edit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  const [categoryName, setCategoryName] = useState('');
  const [categoryKeywords, setCategoryKeywords] = useState('');
  const [subcategories, setSubcategories] = useState([]);

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    const fetchCategory = async () => {
      try {
        const res = await authManager.authenticatedFetch(`${Base_Url}/shopping/categories/${id}`);
        if (!res.ok) throw new Error("Failed to fetch category");
        const data = await res.json();
        const cat = data.result;

        setCategoryName(cat.name || '');
        setCategoryKeywords((cat.keywords || []).join(', '));
        setSubcategories(
          (cat.children || []).map(sub => ({
            _id: sub._id,
            name: sub.name,
            keywords: (sub.keywords || []).join(', '),
            children: (sub.children || []).map(child => ({
              _id: child._id,
              name: child.name,
              keywords: (child.keywords || []).join(', ')
            })),
            showChildren: (sub.children || []).length > 0
          }))
        );
      } catch (err) {
        console.error("Error loading category:", err);
        navigate('/category');
      } finally {
        setLoading(false);
      }
    };

    fetchCategory();
  }, [id, navigate]);

  // ---- Subcategory & Child Handlers ----
  const handleSubChange = (i, field, value) => {
    const updated = [...subcategories];
    updated[i][field] = value;
    setSubcategories(updated);
  };

  const handleChildChange = (i, j, field, value) => {
    const updated = [...subcategories];
    updated[i].children[j][field] = value;
    setSubcategories(updated);
  };

  const addSubcategory = () => {
    setSubcategories([
      ...subcategories,
      { name: '', keywords: '', children: [], showChildren: false }
    ]);
  };

  const addChild = (i) => {
    const updated = [...subcategories];
    updated[i].children.push({ name: '', keywords: '' });
    updated[i].showChildren = true;
    setSubcategories(updated);
  };

  const confirmRemoveSub = (i) => {
    const sub = subcategories[i];
    setDeleteTarget({ type: 'sub', subIndex: i, id: sub._id, name: sub.name });
    setShowDeleteModal(true);
  };

  const confirmRemoveChild = (i, j) => {
    const child = subcategories[i].children[j];
    setDeleteTarget({ type: 'child', subIndex: i, childIndex: j, id: child._id, name: child.name });
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    try {
      if (deleteTarget?.id) {
        // 🔹 Existing category → call API
        const res = await authManager.authenticatedFetch(`${Base_Url}/shopping/categories/${deleteTarget.id}`, {
          method: 'DELETE'
        });
        if (!res.ok) throw new Error("Delete failed");

        toast.success(`${deleteTarget.name} deleted successfully`);
      } else {
        // 🔹 New (unsaved) category → just remove from state
        toast.info(`${deleteTarget?.name || 'New item'} removed locally`);
      }

      // Update UI after removal
      if (deleteTarget.type === 'sub') {
        const updated = [...subcategories];
        updated.splice(deleteTarget.subIndex, 1);
        setSubcategories(updated);
      } else if (deleteTarget.type === 'child') {
        const updated = [...subcategories];
        updated[deleteTarget.subIndex].children.splice(deleteTarget.childIndex, 1);
        setSubcategories(updated);
      }

    } catch (err) {
      console.error("Delete error:", err);
      toast.error('Failed to delete');
    } finally {
      setShowDeleteModal(false);
      setDeleteTarget(null);
    }
  };


  // ---- Submit Update ----
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // --- Update main category ---
      const mainBody = {
        name: categoryName,
        description: '',
        status: 'active',
        keywords: categoryKeywords
          ? categoryKeywords.split(',').map(k => k.trim())
          : []
      };

      const mainRes = await authManager.authenticatedFetch(`${Base_Url}/shopping/categories/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(mainBody)
      });

      if (!mainRes.ok) throw new Error("Main category update failed");
      const mainData = await mainRes.json();
      const mainCategoryId = mainData?.result?._id || id;

      // --- Update or Add subcategories ---
      for (const sub of subcategories) {
        let subId = sub._id;

        if (subId) {
          const subRes = await authManager.authenticatedFetch(`${Base_Url}/shopping/categories/${subId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: sub.name,
              description: '',
              parentId: mainCategoryId,
              status: 'active',
              keywords: sub.keywords
                ? sub.keywords.split(',').map(k => k.trim())
                : []
            })
          });
          if (!subRes.ok) throw new Error(`Failed updating subcategory ${sub.name}`);
          const subData = await subRes.json();
          subId = subData?.result?._id || sub._id;
        } else if (sub.name.trim()) {
          const subRes = await authManager.authenticatedFetch(`${Base_Url}/shopping/categories`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: sub.name,
              description: '',
              parentId: mainCategoryId,
              status: 'active',
              keywords: sub.keywords
                ? sub.keywords.split(',').map(k => k.trim())
                : []
            })
          });
          const subData = await subRes.json();
          subId = subData?.result?._id;
        }

        // --- Update or Add child categories ---
        for (const child of sub.children) {
          if (child._id) {
            await authManager.authenticatedFetch(`${Base_Url}/shopping/categories/${child._id}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                name: child.name,
                description: '',
                parentId: subId,
                status: 'active',
                keywords: child.keywords
                  ? child.keywords.split(',').map(k => k.trim())
                  : []
              })
            });
          } else if (child.name.trim()) {
            await authManager.authenticatedFetch(`${Base_Url}/shopping/categories`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                name: child.name,
                description: '',
                parentId: subId,
                status: 'active',
                keywords: child.keywords
                  ? child.keywords.split(',').map(k => k.trim())
                  : []
              })
            });
          }
        }
      }

      toast.success('Category updated successfully!');
      setTimeout(() => {
        navigate('/category');
      }, 3000);

    } catch (err) {
      console.error("Error updating:", err);
      toast.error(err.message || 'Update failed. Please try again.');
    }
  };


  return (
    <div>
      <ToastContainer autoClose={3000} />
      <div className={`modal fade ${showDeleteModal ? 'show' : ''}`} style={{ display: showDeleteModal ? 'block' : 'none' }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Delete Category</h5>
              <button type="button" className="close" onClick={() => setShowDeleteModal(false)}>
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body text-center">
              Are you sure you want to delete <strong>{deleteTarget?.name}</strong>?
            </div>
            <div className="modal-footer justify-content-center">
              <button className="btn btn-secondary me-2" onClick={() => setShowDeleteModal(false)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDelete}>Yes, delete</button>
            </div>
          </div>
        </div>
      </div>

      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-6">
              <h1 className="m-0 txt_lft">Edit Category</h1>
            </div>
            <div className="col-6 text-right">
              <Link to="/category" className="btn btn-link">
                <i className="fa-solid fa-arrow-left-long" /> Back
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="frmwdth_cntr">
              <div className="col-lg-12">
                <div className="card card-primary card-outline">
                  {loading ? (
                    <div className="card-body text-center">
                      <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <form className="form-horizontal" onSubmit={handleSubmit}>
                      <div className="card-body">
                        <div className="form-group row">
                          <label className="col-md-4 col-form-label">Category</label>
                          <div className="col-md-8">
                            <div className="mb-2">
                              <input type="text" className="form-control"
                                value={categoryName} onChange={e => setCategoryName(e.target.value)} />
                            </div>
                            <div>
                              <input type="text" className="form-control"
                                value={categoryKeywords} onChange={e => setCategoryKeywords(e.target.value)} />
                            </div>
                          </div>
                        </div>

                        <div className="form-group row">
                          <label className="col-md-4 col-form-label">Sub Categories</label>
                          <div className="col-md-8">
                            {subcategories.map((sub, i) => (
                              <div key={i} className="mb-3 p-2 border rounded">
                                <input
                                  type="text"
                                  className="form-control mb-2"
                                  placeholder="Subcategory name"
                                  value={sub.name}
                                  onChange={e => handleSubChange(i, 'name', e.target.value)}
                                />
                                <input
                                  type="text"
                                  className="form-control mb-2"
                                  placeholder="Keywords"
                                  value={sub.keywords}
                                  onChange={e => handleSubChange(i, 'keywords', e.target.value)}
                                />

                                {sub.children.map((child, j) => (
                                  <div key={j} className="ml-3 p-2 border rounded">
                                    <input
                                      type="text"
                                      className="form-control mb-2"
                                      placeholder="Child name"
                                      value={child.name}
                                      onChange={e => handleChildChange(i, j, 'name', e.target.value)}
                                    />
                                    <input
                                      type="text"
                                      className="form-control mb-2"
                                      placeholder="Keywords"
                                      value={child.keywords}
                                      onChange={e => handleChildChange(i, j, 'keywords', e.target.value)}
                                    />
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-dark"
                                      onClick={() => confirmRemoveChild(i, j)}
                                    >
                                      Remove Child
                                    </button>
                                  </div>
                                ))}

                                <div className="d-flex justify-content-start mt-2">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-danger mr-2"
                                    onClick={() => confirmRemoveSub(i)}
                                  >
                                    Remove Subcategory
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-blue"
                                    onClick={() => addChild(i)}
                                  >
                                    + Add Child
                                  </button>
                                </div>
                              </div>
                            ))}
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              onClick={addSubcategory}
                            >
                              + Add Subcategory
                            </button>
                          </div>
                        </div>

                      </div>

                      <div className="card-footer text-center">
                        <button type="submit" className="btn btn-blue">Update</button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Category_Edit;
